import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, Card } from 'react-bootstrap';

import AccordionItem from '../AccordionItem';
import { adminApi } from '../../../api';

function borderClass(terminalLabel) {
  if (terminalLabel === 'success') return 'border-success';
  if (terminalLabel === 'failure') return 'border-danger';
  return 'border-secondary';
}

function eventStatusClass(status) {
  if (status === 'failed' || status === 'service_exception') return 'danger';
  if (status === 'completed' || status === 'ledgered') return 'success';
  return 'primary';
}

function TransferCard({ transfer }) {
  const [expanded, setExpanded] = useState([]);
  const {
    id,
    source_network,
    destination_network,
    provider,
    node_label,
    node_terminal_label,
    reversal_type,
    priority_external_id,
    transfer_events,
    ledger_entry_records,
  } = transfer;

  const handleExpanded = (key) =>
    setExpanded(expanded.includes(key) ? expanded.filter((e) => e !== key) : [...expanded, key]);
  const accordionActiveKey = (key) => expanded.includes(key) && key;
  const { t } = useTranslation();

  const hasEvents = transfer_events && transfer_events.length > 0;
  const hasLedger = ledger_entry_records && ledger_entry_records.length > 0;

  return (
    <Card
      className={`mt-2 border-2 ${borderClass(node_terminal_label)} flex-shrink-0`}
      style={{ width: 280 }}
    >
      <Card.Body className="p-3">
        <div className="fw-semibold mb-2">{`${t(
          'common:transactions.detail.transfers.transfer'
        )} ${id}${node_label ? ` (${node_label})` : ''}`}</div>
        <table className="w-100 small">
          <tbody>
            <tr>
              <td className="text-muted pe-2 text-nowrap align-top">
                {t('common:transactions.detail.transfers.network')}
              </td>
              <td className="text-break">
                {source_network === destination_network
                  ? source_network
                  : `${source_network} → ${destination_network}`}
              </td>
            </tr>
            {provider && (
              <tr>
                <td className="text-muted pe-2 text-nowrap align-top">
                  {t('common:transactions.detail.transfers.provider')}
                </td>
                <td className="text-break">{provider}</td>
              </tr>
            )}
            {reversal_type && (
              <tr>
                <td className="text-muted pe-2 text-nowrap align-top">
                  {t('common:transactions.detail.transfers.reversal')}
                </td>
                <td className="text-break">{reversal_type}</td>
              </tr>
            )}
            {priority_external_id && (
              <tr>
                <td className="text-muted pe-2 text-nowrap align-top">
                  {t('common:labels.externalId')}
                </td>
                <td className="font-monospace text-break" style={{ fontSize: '0.7rem' }}>
                  {priority_external_id}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card.Body>

      {hasEvents && (
        <Accordion activeKey={accordionActiveKey(1)}>
          <AccordionItem
            small
            className="rounded-0 border-0 border-top shadow-none"
            eventKey={1}
            label={`${t('common:transactions.detail.transfers.events')} (${
              transfer_events.length
            })`}
            expanded={expanded}
            onSetExpanded={handleExpanded}
          >
            <div className="px-3 py-2">
              {transfer_events.map((ev) => (
                <div
                  key={`${ev.status}-${ev.timestamp}`}
                  className={transfer_events.indexOf(ev) > 0 ? 'mt-2 pt-2 border-top' : ''}
                >
                  <table className="w-100 small">
                    <tbody>
                      <tr>
                        <td className="text-muted pe-2 text-nowrap align-top">
                          {t('common:transactions.detail.transfers.status')}
                        </td>
                        <td className={`fw-semibold text-${eventStatusClass(ev.status)}`}>
                          {ev.status}
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted pe-2 text-nowrap align-top">
                          {t('common:transactions.detail.transfers.time')}
                        </td>
                        <td className="font-monospace">{new Date(ev.timestamp).toISOString()}</td>
                      </tr>
                      {ev.transfer_node ? (
                        <tr>
                          <td className="text-muted pe-2 text-nowrap align-top">
                            {t('common:transactions.detail.transfers.node')}
                          </td>
                          <td className="text-break">{ev.transfer_node}</td>
                        </tr>
                      ) : null}
                      {ev.detail ? (
                        <tr>
                          <td className="text-muted pe-2 text-nowrap align-top">
                            {t('common:transactions.detail.transfers.detail')}
                          </td>
                          <td
                            className="font-monospace text-break"
                            style={{ wordBreak: 'break-all' }}
                          >
                            {ev.detail}
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </AccordionItem>
        </Accordion>
      )}

      {hasLedger && (
        <Accordion activeKey={accordionActiveKey(2)}>
          <AccordionItem
            small
            className="rounded-0 border-0 border-top shadow-none"
            eventKey={2}
            label={`${t('common:transactions.detail.transfers.ledger')} (${
              ledger_entry_records.length
            })`}
            expanded={expanded}
            onSetExpanded={handleExpanded}
          >
            <div className="px-3 py-2">
              {ledger_entry_records.map((entry, i) => (
                <div key={entry.id} className={i > 0 ? 'mt-2 pt-2 border-top' : ''}>
                  <table className="w-100 small">
                    <tbody>
                      <tr>
                        <td className="text-muted pe-2 text-nowrap align-top">
                          {t('common:transactions.detail.transfers.operation')}
                        </td>
                        <td className="fw-semibold text-primary">{entry.operation}</td>
                      </tr>
                      <tr>
                        <td className="text-muted pe-2 text-nowrap align-top">
                          {t('common:transactions.detail.transfers.layer')}
                        </td>
                        <td className="text-break">{entry.layer}</td>
                      </tr>
                      <tr>
                        <td className="text-muted pe-2 text-nowrap align-top">
                          {t('common:transactions.detail.transfers.transcode')}
                        </td>
                        <td className="text-break" style={{ wordBreak: 'break-all' }}>
                          {entry.transcode}
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted pe-2 text-nowrap align-top">
                          {t('common:transactions.detail.transfers.time')}
                        </td>
                        <td className="font-monospace">
                          {new Date(entry.recorded_at).toISOString()}
                        </td>
                      </tr>
                      {entry.memo ? (
                        <tr>
                          <td className="text-muted pe-2 text-nowrap align-top">
                            {t('common:transactions.detail.transfers.memo')}
                          </td>
                          <td className="fst-italic text-break">{entry.memo}</td>
                        </tr>
                      ) : null}
                      {entry.settled_balance != null ? (
                        <tr>
                          <td className="text-muted pe-2 text-nowrap align-top">
                            {t('common:transactions.detail.transfers.settledBalance')}
                          </td>
                          <td>
                            {Number(entry.settled_balance).toLocaleString('en-US', {
                              style: 'currency',
                              currency: 'USD',
                            })}
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </AccordionItem>
        </Accordion>
      )}
    </Card>
  );
}

function TransactionTransfers({ transactionId }) {
  const { t } = useTranslation();
  const [transfers, setTransfers] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchTransfers = async () => {
      try {
        const response = await adminApi.getTransactionTransfers(transactionId);
        if (response.success) setTransfers(response.transfers);
        else setError(true);
      } catch (err) {
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTransfers();
  }, [transactionId]);

  if (isLoading)
    return <p className="text-muted">{t('common:transactions.detail.transfers.loading')}</p>;
  if (error)
    return <p className="text-warning">{t('common:transactions.detail.transfers.error')}</p>;
  if (!transfers || transfers.length === 0)
    return <p className="text-muted">{t('common:transactions.detail.transfers.empty')}</p>;

  return (
    <div className="d-flex flex-row flex-wrap align-items-start" style={{ gap: '8px 0' }}>
      {transfers.map((tfr, i) => (
        <React.Fragment key={tfr.id}>
          {i > 0 && (
            <div
              className="align-self-start text-muted px-2"
              style={{ marginTop: 18, fontSize: 18 }}
            >
              &rarr;
            </div>
          )}
          <TransferCard transfer={tfr} />
        </React.Fragment>
      ))}
    </div>
  );
}

export default TransactionTransfers;
